// Spec §4.4 — modal form to add a SectionSyllabusEntry.
// Uses the existing `GET /api/school/subjects/<id>/chapters/` endpoint
// (via getSubjectChaptersApi) — no new backend endpoint required.
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useCreateSyllabusEntry } from '../../../api/hooks';
import { useAuth } from '../../../../utils/Auth/AuthProvider';
import { useSchoolGradeSubjects } from '../../../../react-query/queries';
import { getSubjectChaptersApi } from '../../../../utils/remote';
import Modal from '../../../components/ui/Modal';
import FormField from '../../../components/ui/FormField';
import Button from '../../../components/ui/Button';
import PillTabs from '../../../components/ui/PillTabs';

interface Props {
  gradeId: number;
  sectionId: number;
  defaultWeek: string;
  onClose: () => void;
}

const FIELD = 'min-h-[44px] w-full rounded-[12px] border border-line-strong bg-surface px-3 py-2 text-sm text-ink outline-none transition focus:border-ink focus:ring-4 focus:ring-accent/40';

interface ChapterRow { id: number; name: string }

export default function SyllabusEntryForm({
  gradeId, sectionId, defaultWeek, onClose,
}: Props) {
  const { token } = useAuth();
  const { data: subjData } = useSchoolGradeSubjects(token, String(gradeId));
  // The grade-subjects endpoint sends `subject_name`; keep `name` for older shapes.
  const subjects: Array<{ id: number; name: string }> =
    (((subjData as unknown as { subjects?: Array<{ id: number; name?: string; subject_name?: string }> })?.subjects) ?? [])
      .map((s) => ({ id: s.id, name: s.subject_name || s.name || '' }));

  const [subjectId, setSubjectId] = useState<number | ''>('');
  const [chapterId, setChapterId] = useState<number | ''>('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  const chaptersQ = useQuery({
    queryKey: ['school', 'chapters-by-subject', subjectId, gradeId],
    queryFn: () => getSubjectChaptersApi(token, Number(subjectId), gradeId),
    enabled: subjectId !== '',
  });

  useEffect(() => { setChapterId(''); }, [subjectId]);

  const create = useCreateSyllabusEntry({ grade: gradeId, section: sectionId });

  async function submit(ev?: React.FormEvent | React.MouseEvent) {
    ev?.preventDefault();
    setError(null);
    if (subjectId === '' || chapterId === '') {
      setError('Subject and chapter required.');
      return;
    }
    try {
      await create.mutateAsync({
        section: sectionId,
        subject: Number(subjectId),
        chapter: Number(chapterId),
        week_start: defaultWeek,
        notes: notes.trim() || undefined,
      });
      onClose();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      setError(err?.response?.data?.detail ?? 'Failed to add entry.');
    }
  }

  const chapters: ChapterRow[] = chaptersQ.data?.chapters ?? [];

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Add syllabus entry · Week of ${defaultWeek}`}
      size="md"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} type="button">Cancel</Button>
          <Button
            variant="primary"
            onClick={(e) => submit(e)}
            loading={create.isPending}
            type="button">
            Add
          </Button>
        </>
      )}>
      <form onSubmit={submit} className="space-y-3">
        <FormField label="Subject" required>
          <PillTabs scroll dense label="Subject" value={String(subjectId)}
            onChange={(k) => setSubjectId(Number(k))}
            options={subjects.map((s) => ({ key: String(s.id), label: s.name }))} />
        </FormField>

        {subjectId !== '' && (
          <FormField label="Chapter" required>
            {chaptersQ.isLoading ? (
              <p className="text-sm text-muted">Loading chapters…</p>
            ) : chapters.length === 0 ? (
              <p className="text-sm text-muted">No chapters for this subject yet.</p>
            ) : (
              <PillTabs scroll dense label="Chapter" value={String(chapterId)}
                onChange={(k) => setChapterId(Number(k))}
                options={chapters.map((c) => ({ key: String(c.id), label: c.name }))} />
            )}
          </FormField>
        )}

        <FormField label="Notes" htmlFor="syllabus-notes" hint="Optional">
          <input
            id="syllabus-notes"
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={512}
            className={FIELD} />
        </FormField>

        {error && <p className="text-xs text-danger">{error}</p>}
      </form>
    </Modal>
  );
}
